<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import BaseSvg from '@clovyr/bed/components/icons/BaseSvg.vue';

import featureAnyLlmIcon from '@clovyr/bed/assets/images/icons/ai-landing/feature-any-llm.svg';
import featureDataIntegrationsIcon from '@clovyr/bed/assets/images/icons/ai-landing/feature-data-integrations.svg';
import featureDeployIcon from '@clovyr/bed/assets/images/icons/ai-landing/feature-deploy.svg';
import featureMcpIcon from '@clovyr/bed/assets/images/icons/ai-landing/feature-mcp.svg';
import featureSlashIcon from '@clovyr/bed/assets/images/icons/ai-landing/feature-slash.svg';
import featureVectorDbIcon from '@clovyr/bed/assets/images/icons/ai-landing/feature-vector-db.svg';
import featureWorkspacesIcon from '@clovyr/bed/assets/images/icons/ai-landing/feature-workspaces.svg';
import featureZeroTrustIcon from '@clovyr/bed/assets/images/icons/ai-landing/feature-zero-trust.svg';

type Feature = {
  iconSrc: string;
  color: string;
  title: string;
  description: string;
};

const features: Feature[] = [
  {
    iconSrc: featureZeroTrustIcon,
    color: '#00bf74',
    title: 'Zero-Trust Privacy',
    description:
      'Your documents, conversations, and model weights never leave your infrastructure. No telemetry. No third-party data sharing. Full sovereignty.',
  },
  {
    iconSrc: featureAnyLlmIcon,
    color: '#a78bfa',
    title: 'Any LLM, Any Time',
    description:
      'Swap between OpenAI, Anthropic, Llama, Mistral, and dozens more without changing your workflow. Local or hosted, you decide.',
  },
  {
    iconSrc: featureDataIntegrationsIcon,
    color: '#38bdf8',
    title: 'Data Integrations',
    description:
      'Connect your existing cloud storage and document platforms directly. Agents read, search, and reason over your files without manual uploads.',
  },
  {
    iconSrc: featureMcpIcon,
    color: '#f472b6',
    title: 'MCP App Connections',
    description:
      'Agents tap into your business tools via Model Context Protocol. Query live data across your stack without copying it anywhere.',
  },
  {
    iconSrc: featureWorkspacesIcon,
    color: '#fb923c',
    title: 'Multi-User Workspaces',
    description:
      'Invite your team with granular role-based access. Admins control which models, documents, and features each user can access.',
  },
  {
    iconSrc: featureVectorDbIcon,
    color: '#00e874',
    title: 'Vector DB Support',
    description:
      'Built-in support for Chroma, Pinecone, Weaviate, Qdrant, and more. Bring your existing vector store or use the embedded one out of the box.',
  },
  {
    iconSrc: featureSlashIcon,
    color: '#fbbf24',
    title: 'Slash Commands & Agents',
    description:
      'Supercharge workflows with slash commands, AI agents, and a developer API. Build custom integrations or use community-built skills.',
  },
  {
    iconSrc: featureDeployIcon,
    color: '#00bf74',
    title: '1-Click Deploy on Clovyr',
    description:
      'Clovyr handles provisioning, networking, and updates on your behalf. Go from zero to private AI in under five minutes.',
  },
];

const sectionVisible = ref(false);
const visibleCards = ref<boolean[]>(features.map(() => false));
const sectionRef = ref<HTMLElement | null>(null);
const cardRefs = ref<(HTMLElement | null)[]>([]);

let sectionObserver: IntersectionObserver | undefined;
let cardObserver: IntersectionObserver | undefined;

const setCardRef = (index: number, element: unknown) => {
  cardRefs.value[index] = element instanceof Element ? (element as HTMLElement) : null;
};

onMounted(() => {
  sectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          sectionVisible.value = true;
          sectionObserver?.disconnect();
        }
      });
    },
    {
      threshold: 0.15,
    },
  );

  if (sectionRef.value) {
    sectionObserver.observe(sectionRef.value);
  }

  cardObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        const index = Number((entry.target as HTMLElement).dataset.index);
        visibleCards.value[index] = true;
        cardObserver?.unobserve(entry.target);
      });
    },
    {
      rootMargin: '0px 0px -40px 0px',
      threshold: 0.1,
    },
  );

  cardRefs.value.forEach((card) => {
    if (card) {
      cardObserver?.observe(card);
    }
  });
});

onBeforeUnmount(() => {
  sectionObserver?.disconnect();
  cardObserver?.disconnect();
});
</script>

<template>
  <section ref="sectionRef" class="ai-features">
    <div class="ai-features__grid-overlay"></div>
    <div class="ai-features__inner">
      <header class="ai-features__head" :class="{ 'is-visible': sectionVisible }">
        <span class="ai-features__eyebrow">Everything you need</span>
        <h2 class="ai-features__title">
          AI that works for you,
          <span> not against you.</span>
        </h2>
        <p class="ai-features__copy">
          Most AI tools trade your data for convenience. Clovyr AI Cloud gives you both without the
          compromise.
        </p>
      </header>

      <div class="ai-features__grid">
        <article
          v-for="(feature, idx) in features"
          :key="feature.title"
          :ref="(element) => setCardRef(idx, element as Element)"
          class="ai-feature-card"
          :class="{ 'is-visible': visibleCards[idx] }"
          :data-index="idx"
          :style="{ '--delay': `${(idx % 4) * 80}ms`, '--feature-color': feature.color }"
        >
          <div class="ai-feature-card__icon-wrap">
            <BaseSvg
              class="ai-feature-card__icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              role="img"
            >
              <image
                :href="feature.iconSrc"
                x="0"
                y="0"
                width="100%"
                height="100%"
                preserveAspectRatio="xMidYMid meet"
              />
            </BaseSvg>
          </div>
          <h3>{{ feature.title }}</h3>
          <p>{{ feature.description }}</p>
        </article>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use '../../styles/components/ai-landing-shared' as shared;

.ai-features {
  position: relative;
  background: #221a33;
  padding: 7rem 2rem;
}

.ai-features__grid-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.025;
  background-image:
    linear-gradient(#00bf74 1px, transparent 1px),
    linear-gradient(90deg, #00bf74 1px, transparent 1px);
  background-size: 60px 60px;
}

.ai-features__inner {
  position: relative;
  max-width: 1280px;
  margin: 0 auto;
}

.ai-features__head {
  @include shared.section-head(4rem, 20px);
}

.ai-features__head.is-visible {
  @include shared.section-head-visible(fadeLift, 0.55s);
}

.ai-features__eyebrow {
  @include shared.eyebrow;

  display: inline-block;
}

.ai-features__title {
  @include shared.section-title;
}

.ai-features__title span {
  @include shared.accent-span;
}

.ai-features__copy {
  @include shared.section-copy(600px);
}

.ai-features__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

.ai-feature-card {
  --feature-color: #00bf74;
  background: #2a1f40;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 1rem;
  padding: 1.5rem;
  opacity: 0;
  transform: translateY(24px);
  transition:
    border-color 300ms ease,
    background-color 300ms ease,
    transform 300ms ease;
}

.ai-feature-card.is-visible {
  animation: fadeLiftCard 0.5s ease var(--delay, 0ms) forwards;
}

.ai-feature-card:hover {
  transform: translateY(-3px);
  border-color: rgba(255, 255, 255, 0.15);
  background: #2e2448;
}

.ai-feature-card__icon-wrap {
  display: flex;
  width: 2.5rem;
  height: 2.5rem;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  border-radius: 0.75rem;
  background: color-mix(in srgb, var(--feature-color) 8%, transparent);
}

.ai-feature-card__icon {
  width: 1.25rem;
  height: 1.25rem;
  color: var(--feature-color);
}

.ai-feature-card h3 {
  margin: 0;
  color: #fff;
  font-size: 1.03rem;
  font-weight: 700;
}

.ai-feature-card p {
  margin: 0.5rem 0 0;
  color: #6b7280;
  font-size: 0.85rem;
  line-height: 1.65;
  font-weight: 500;
}

@media (min-width: 640px) {
  .ai-features__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .ai-features__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (min-width: 1280px) {
  .ai-features__grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 1023px) {
  .ai-features__head {
    margin-bottom: 2.8rem;
  }
}

@media (max-width: 800px) {
  .ai-features {
    padding: 5.5rem 1.5rem;
  }
}

@media (max-width: 560px) {
  .ai-features {
    padding: 5rem 1rem;
  }

  .ai-features__head {
    margin-bottom: 2.2rem;
  }

  .ai-feature-card {
    padding: 1.15rem;
  }

  .ai-feature-card h3 {
    font-size: 0.95rem;
  }

  .ai-feature-card p {
    font-size: 0.8rem;
    line-height: 1.55;
  }
}

@include shared.keyframes-fade-lift(fadeLift, 20px);

@keyframes fadeLiftCard {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
